import { Component, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { PageSidesheetActions } from '@next/core-lib/components';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, ReplaySubject } from 'rxjs';
import { map, startWith, take, takeUntil } from 'rxjs/operators';

import { PageTitles, SiteState, TotalNitrogenLegalNorm, TotalNitrogenLegalNormFilter } from '../../../shared/models';
import { DisplayService } from '../../../shared/services/display.service';
import {
  AddTotalNitrogenLegalNorm,
  ClearTotalNitrogenLegalNormFilter,
  DeleteTotalNitrogenLegalNorm,
  EditTotalNitrogenLegalNorm,
  LoadTotalNitrogenLegalNormsRequest,
  OpenTotalNitrogenLegalNormImportForm,
  UpdateTotalNitrogenLegalNormFilter,
} from '../../../store/actions';
import {
  getSoilTypes,
  getTotalNitrogenLegalNormFilter,
  getTotalNitrogenLegalNorms,
  getTotalNitrogenLegalNormTotalNumberOfItems,
} from '../../../store/selectors';

@Component({
  selector: 'app-total-nitrogen-legal-norm-list',
  templateUrl: './total-nitrogen-legal-norm-list.component.html',
  standalone: false,
})
export class TotalNitrogenLegalNormListComponent implements OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly breakpoint = inject<BreakpointService>(BreakpointServiceInjector);
  readonly displayService = inject(DisplayService);
  private readonly dialog = inject(DialogService);
  private readonly locale = inject(LocaleService);

  @ViewChild('paginator', { static: false }) paginator: MatPaginator;
  title = PageTitles.TotalNitrogenLegalNorm;
  tableConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'calendarYear',
        label: this.locale.translate('year'),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'agrifirmCropId',
        label: this.locale.translate('columnHeader.agrifirmCropId'),
        type: TableColumnTypes.NUMBER,
        disableFormatting: true,
      },
      {
        id: 'cropName',
        label: this.locale.translate('cropName'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'soilTypeString',
        label: this.locale.translate('soilType'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'locationString',
        label: this.locale.translate('location'),
        type: TableColumnTypes.STRING,
      },
      {
        id: 'nitrogenNorm',
        label: this.locale.translate('norm'),
        type: TableColumnTypes.STRING,
      },
    ],
    actions: [
      {
        id: 'edit',
        label: this.locale.translate('edit'),
        icon: 'edit',
        callback: (event: TotalNitrogenLegalNorm) => this.editNorm(event),
        disabled: () => false,
      },
      {
        id: 'delete',
        label: this.locale.translate('delete'),
        icon: 'delete',
        callback: (event: TotalNitrogenLegalNorm) => this.deleteNorm(event),
        disabled: () => false,
      },
    ],
  };
  filterControl = new FormControl();
  defaultPageSize = 10;
  pageSizeOptions = [10, 25, 50];

  request: { pageNumber: number; pageSize: number; filter: TotalNitrogenLegalNormFilter } = {
    pageNumber: 1,
    pageSize: 10,
    filter: {},
  };

  totalNumberOfItems$ = this.store.pipe(select(getTotalNitrogenLegalNormTotalNumberOfItems));

  norms$ = this.store.pipe(select(getTotalNitrogenLegalNorms));

  soilTypes$ = this.store.pipe(select(getSoilTypes));

  filterValue$ = this.filterControl.valueChanges.pipe(
    map((value: string) => value || ''),
    startWith(''),
  );

  filterEnabled$ = new BehaviorSubject(false);
  isMobile$ = this.breakpoint.ltLg$;
  isEmpty$ = combineLatest([this.filterEnabled$, this.totalNumberOfItems$]).pipe(
    map(([filterEnabled, totalNumberOfItems]) => !filterEnabled && totalNumberOfItems === 0),
  );

  totalNitrogenLegalNormFilter$ = this.store.pipe(select(getTotalNitrogenLegalNormFilter));

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  ngOnInit() {
    this.norms$.pipe(takeUntil(this.destroyed$)).subscribe((norms) => {
      this.tableConfig.dataSource.data = norms.map((norm) => ({
        ...norm,
        soilTypeString: this.getSoilTypeName(norm.soilTypeId),
        locationString: this.getLocationString(norm.location),
      }));
    });

    this.updateData();
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  updateData() {
    this.store.dispatch(new LoadTotalNitrogenLegalNormsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  updateFilter(data: TotalNitrogenLegalNormFilter) {
    this.request.filter = data;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.request.pageNumber = 1;
    this.store.dispatch(new UpdateTotalNitrogenLegalNormFilter(data));
    this.store.dispatch(new LoadTotalNitrogenLegalNormsRequest(this.request.pageNumber, this.request.pageSize, this.request.filter));
  }

  setFilterEnabled(isEnabled: boolean) {
    this.filterEnabled$.next(isEnabled);
  }

  resetFilter() {
    this.store.dispatch(new ClearTotalNitrogenLegalNormFilter());
  }

  onPage(event: PageEvent) {
    this.request = {
      ...this.request,
      pageNumber: event.pageIndex + 1,
      pageSize: event.pageSize,
    };
    this.updateData();
  }

  addNorm() {
    this.store.dispatch(new AddTotalNitrogenLegalNorm());
    if (this.paginator) {
      (this.paginator.length as number)++;
    }
  }

  editNorm(norm: TotalNitrogenLegalNorm) {
    this.store.dispatch(new EditTotalNitrogenLegalNorm(norm));
  }

  deleteNorm(norm: TotalNitrogenLegalNorm) {
    this.dialog
      .confirm({
        title: this.locale.translate('delete'),
        text: this.locale.translate('deleteTotalNitrogenLegalNorm'),
        pushHistoryState: false,
      })
      .pipe(take(1))
      .subscribe((x) => {
        if (x.status === DIALOG_RETURN_TYPES.confirm) {
          this.store.dispatch(new DeleteTotalNitrogenLegalNorm(norm));
          if (this.paginator) {
            this.paginator.pageIndex = 0;
            (this.paginator.length as number)--;
          }
          this.request.pageNumber = 1;
          this.updateData();
        }
      });
  }

  importTotalNitrogenLegalNorms() {
    this.store.dispatch(new OpenTotalNitrogenLegalNormImportForm());
  }

  setSearch() {
    this.store.dispatch(PageSidesheetActions.openSidesheet());
  }

  getLocationString = (key: string): string => this.displayService.getLocationString(key);

  getGrasslandUsageString = (key: string): string => this.displayService.getGrasslandUsageString(key);

  getCropVarietySpecificNormString = (key: string): string => this.displayService.getCropVarietySpecificNormString(key);

  getSoilTypeName = (soilTypeId: number): string => {
    let soilTypeName = '';
    this.soilTypes$.subscribe((soilTypes) => (soilTypeName = soilTypes?.find((s) => s.id === soilTypeId)?.name));
    return soilTypeName;
  };
}
